import { Button, Chip, TextField, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { ApprovalPinModal } from "src/components/DocViewer/components/docInfoDrawer/approversTab/pin/ApprovalPinModal";
import { FieldLabel } from "src/components/ui/FieldLabel";
import { UserSelect } from "src/components/ui/UserSelect";
import { OrgScopedBaseUser, User } from "src/stores/models/user";
import {
  isApprovalSelection,
  isQualityEventApproval,
  QualityEventApprovalDecision,
  QualityEventApprovalSelection,
  QualityEventField,
  QualityEventRecord,
} from "src/types/quality-events";
import { getUserDisplayName } from "src/utils/user";

const approvalStatus = {
  APPROVED: { status: "Approved", color: "success" },
  REJECTED: { status: "Rejected", color: "error" },
  PENDING: { status: "Waiting for approval", color: "primary" },
} as const;

export const QualityEventApprovalControls = ({
  record,
  stepKey,
  field,
  value,
  users,
  userId,
  currentUser,
  disabled = false,
  readOnly = false,
  dirty = false,
  approveDisabled = false,
  showOutstanding = false,
  required = field.required,
  onChange,
  onDecision,
}: {
  record: QualityEventRecord;
  stepKey: string;
  field: QualityEventField;
  value?: unknown;
  users: OrgScopedBaseUser[];
  userId: string;
  currentUser?: User;
  disabled?: boolean;
  readOnly?: boolean;
  dirty?: boolean;
  approveDisabled?: boolean;
  showOutstanding?: boolean;
  required?: boolean;
  onChange?: (value: QualityEventApprovalSelection | null) => void;
  onDecision?: (decision: QualityEventApprovalDecision) => Promise<void>;
}) => {
  const [comment, setComment] = useState("");
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const answer = record.steps[stepKey]?.answers[field.key];
  const saved = isQualityEventApproval(answer) ? answer : undefined;
  const savedApproverName = getUserDisplayName(
    users.find((user) => user.id === saved?.approver) ?? {}
  );
  const activeUsers = users.filter((user) =>
    user.organizationMemberships?.some((membership) => membership.activated)
  );
  const selection = isApprovalSelection(value) ? value : null;
  const selectionDisabled = disabled || readOnly;
  const decisionDisabled = disabled || dirty;
  const canReview =
    onDecision &&
    currentUser &&
    record.status !== "CLOSED" &&
    saved?.status === "PENDING" &&
    saved.approver === userId;
  let outstanding: string | undefined;
  if (
    showOutstanding &&
    (required || saved?.status === "PENDING") &&
    saved?.status !== "APPROVED"
  ) {
    if (!selection?.approver)
      outstanding = "Select an approver to complete this step.";
    else if (selection.approver !== saved?.approver)
      outstanding = "Save to request approval.";
    else if (saved.status === "REJECTED")
      outstanding =
        "Approval was rejected. Update this step and request approval again.";
    else outstanding = `Awaiting approval from ${savedApproverName}.`;
  }
  return (
    <>
      <section className="flex flex-col gap-4 rounded-md bg-color-surface px-[10.5px] py-[7px] [container-type:inline-size]">
        <Typography variant="body2">
          <FieldLabel
            edit={!!onChange}
            label={field.label}
            required={required}
            outstanding={outstanding}
          />
        </Typography>
        {onChange && saved && (
          <Typography variant="body1">
            {saved.status === "PENDING"
              ? "Awaiting approval"
              : approvalStatus[saved.status].status}{" "}
            · {savedApproverName}
            {saved.comment && ` — ${saved.comment}`}
          </Typography>
        )}
        {onChange ? (
          <div className="grid grid-cols-1 gap-[16px] [@container(min-width:640px)]:grid-cols-2">
            <UserSelect
              label={`${field.label} approver`}
              value={users.find((user) => user.id === selection?.approver)}
              users={activeUsers}
              emptyLabel="Select approver"
              disableClear={!!saved}
              disabled={selectionDisabled}
              className="min-w-0"
              onChange={(approver) =>
                onChange(
                  approver
                    ? {
                        approver: approver.id,
                        dueDate: selection?.dueDate ?? null,
                      }
                    : null
                )
              }
            />
            <TextField
              size="small"
              type="date"
              label={`${field.label} due date`}
              value={selection?.dueDate ?? ""}
              InputLabelProps={{ shrink: true }}
              disabled={selectionDisabled}
              className="min-w-0"
              onChange={(event) =>
                onChange({
                  approver: selection?.approver ?? null,
                  dueDate: event.target.value || null,
                })
              }
            />
          </div>
        ) : saved ? (
          <dl className="m-0 flex flex-col gap-4">
            <div>
              <Typography component="dt" variant="body2">
                Approver
              </Typography>
              <dd className="m-0 mt-1 flex flex-wrap items-center gap-2">
                <Typography variant="body1">{savedApproverName}</Typography>
                <Chip
                  size="tiny"
                  label={approvalStatus[saved.status].status}
                  color={approvalStatus[saved.status].color}
                />
              </dd>
            </div>
            <div>
              <Typography component="dt" variant="body2">
                Due
              </Typography>
              <Typography component="dd" variant="body1" className="m-0 mt-1">
                {saved.dueDate ?? "Not provided"}
              </Typography>
            </div>
            {saved.comment && (
              <div>
                <Typography component="dt" variant="body2">
                  Decision comment
                </Typography>
                <Typography component="dd" variant="body1" className="m-0 mt-1">
                  {saved.comment}
                </Typography>
              </div>
            )}
          </dl>
        ) : (
          <Typography variant="body1">Not requested</Typography>
        )}
        {canReview && (
          <>
            {dirty && (
              <Typography variant="caption" color="text.secondary">
                Save your changes before reviewing.
              </Typography>
            )}
            <TextField
              label="Decision comment"
              size="small"
              disabled={decisionDisabled}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={decisionDisabled}
                onClick={() =>
                  void onDecision({
                    requestId: saved.requestId,
                    decision: "REJECTED",
                    comment,
                  })
                }
              >
                Reject
              </Button>
              <Tooltip
                title={
                  approveDisabled
                    ? "Complete all required fields in this step before approving."
                    : ""
                }
                describeChild
                arrow
              >
                <span tabIndex={approveDisabled ? 0 : undefined}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={decisionDisabled || approveDisabled}
                    onClick={() => setPinModalOpen(true)}
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
            </div>
          </>
        )}
      </section>
      {currentUser && onDecision && saved && (
        <ApprovalPinModal
          isOpen={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
          user={currentUser}
          description="Use your four-digit approval PIN to confirm this decision."
          saveText="Approve"
          confirmationWarningText="By approving, you confirm that you reviewed the completed step and approve its contents."
          onApprove={async (pin) => {
            if (!pin) throw new Error("Approval PIN is required.");
            await onDecision({
              requestId: saved.requestId,
              decision: "APPROVED",
              comment,
              pin,
            });
          }}
        />
      )}
    </>
  );
};
